import React from 'react';
import PropTypes from 'prop-types';
import { Label, Icon, Popup } from 'semantic-ui-react';
import { getNormalizeStatus, deriveNormalizeStatusKey } from './normalizeStatusConfig';

/**
 * NormalizeStatusBadge
 *
 * Renders the pre-clone canonicalization state for a VoiceSnippet. Pairs
 * with <CloneStatusBadge> on the pipeline page — the two are stacked so
 * the Book Creator can see both halves of the flow at a glance:
 *
 *     Normalize  Clone
 *    ┌─────────┐ ┌────────┐
 *    │ Ready   │ │Success │   ← happy path
 *    └─────────┘ └────────┘
 *
 *    ┌─────────┐ ┌────────┐
 *    │ Failed  │ │  —     │   ← normalizer failed; clone can't start
 *    └─────────┘ └────────┘
 *
 * Accepts either a raw status string (`statusKey`) or the full `normalize`
 * block returned by `GET /voice-clone-pipeline` so callers don't have to
 * duplicate the derive-missing-vs-none logic.
 */
const NormalizeStatusBadge = ({
  statusKey,
  normalize,
  size = 'tiny',
  showTooltip = true
}) => {
  const key = statusKey || deriveNormalizeStatusKey(normalize);
  const s = getNormalizeStatus(key);

  // Tooltip content: description + the precise server error when failed.
  const tooltip = normalize?.error
    ? `${s.description}\n\nError: ${normalize.error}`
    : s.description;

  const label = (
    <Label
      size={size}
      color={s.color}
      style={{
        minWidth: '112px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '4px',
        lineHeight: 1
      }}
    >
      <Icon
        name={s.icon}
        style={{
          margin: 0,
          height: '1em',
          lineHeight: '1em',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      />
      <span
        style={{
          height: '1em',
          lineHeight: '1em',
          display: 'inline-flex',
          alignItems: 'center'
        }}
      >
        {s.label}
      </span>
    </Label>
  );

  if (!showTooltip) return label;

  return (
    <Popup
      trigger={label}
      content={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>}
      position="top center"
      size="small"
      on={['hover', 'focus']}
    />
  );
};

NormalizeStatusBadge.propTypes = {
  statusKey: PropTypes.string,
  normalize: PropTypes.shape({
    status: PropTypes.string,
    error: PropTypes.string,
    attempts: PropTypes.number
  }),
  size: PropTypes.string,
  showTooltip: PropTypes.bool
};

export default NormalizeStatusBadge;
